/**
 * Auth refresh diagnostics — logging only. Every export is defensive;
 * callers must still wrap in try/catch when in hot auth paths.
 */
import { tokenFpSync } from './authTokens';
import { diagnosticLog } from '../utils/diagnosticLogger';

export type RefreshFailureClass = 'fatal' | 'already_used' | 'transient';

export function parseRefreshErrorDetails(data: unknown): { errorCode: string; message: string } {
  try {
    if (!data || typeof data !== 'object') {
      return { errorCode: 'unknown', message: '' };
    }
    const d = data as Record<string, unknown>;
    const message = String(d.error_description || d.error || d.msg || '');
    let errorCode = String(d.error_code || d.code || '').trim();
    const lower = message.toLowerCase();
    if (!errorCode) {
      if (lower.includes('already_used') || lower.includes('refresh_token_already_used') || lower.includes('already used')) {
        errorCode = 'already_used';
      } else if (lower.includes('refresh_token_not_found')) {
        errorCode = 'refresh_token_not_found';
      } else if (lower.includes('invalid_refresh_token') || lower.includes('invalid refresh token')) {
        errorCode = 'invalid_refresh_token';
      } else if (lower.includes('expired')) {
        errorCode = 'expired';
      } else {
        errorCode = 'unknown';
      }
    }
    return { errorCode, message };
  } catch {
    return { errorCode: 'parse_error', message: '' };
  }
}

/** Supabase / worker rejected the refresh token — not a disk-desync retry case. */
export function isDeadRefreshTokenResponse(status: number, data: unknown): boolean {
  if (status !== 400 && status !== 401 && status !== 403) return false;
  const { errorCode, message } = parseRefreshErrorDetails(data);
  const lower = message.toLowerCase();
  return (
    errorCode === 'invalid_refresh_token'
    || errorCode === 'refresh_token_not_found'
    || errorCode === 'refresh_rejected'
    || lower.includes('refresh token is not valid')
    || lower.includes('invalid refresh token')
    || lower.includes('refresh_token_not_found')
    || lower.includes('invalid_grant')
  );
}

export function classifyRefreshFailure(status: number, data: unknown): RefreshFailureClass {
  try {
    if (status === 503) return 'transient';
    if (data && typeof data === 'object' && (data as Record<string, unknown>).transient) {
      return 'transient';
    }
    const { errorCode, message } = parseRefreshErrorDetails(data);
    const lower = message.toLowerCase();
    if (
      errorCode === 'already_used'
      || errorCode.includes('already_used')
      || lower.includes('already_used')
      || lower.includes('refresh_token_already_used')
      || lower.includes('already used')
    ) {
      return 'already_used';
    }
    if (status === 401 || status === 403) return 'fatal';
    return 'transient';
  } catch {
    return 'transient';
  }
}

/** auth.json restore: 401 while connector still has creds is a desync, not terminal. */
export function classifyAuthJsonRestoreFailure(
  status: number,
  data: unknown,
  connectorHasValidCreds: boolean,
): RefreshFailureClass {
  if (isDeadRefreshTokenResponse(status, data)) return 'fatal';
  const base = classifyRefreshFailure(status, data);
  if (base === 'already_used') return 'already_used';
  if (connectorHasValidCreds && (status === 401 || status === 403)) return 'transient';
  return base;
}

export function logAuthJsonRecoverySuccess(
  via: string,
  oldFp: string,
  newFp: string,
  source?: string,
): void {
  try {
    const sourcePart = source ? ` source=${source}` : '';
    console.info(
      `[Auth:lifecycle] auth.json recovery succeeded via=${via} old_fp=${oldFp} new_fp=${newFp}${sourcePart}`,
    );
  } catch { /* noop */ }
}

export function readLocalStorageRefreshFp(): string {
  try {
    const stored = localStorage.getItem('maestro_auth');
    if (!stored) return 'none';
    const parsed = JSON.parse(stored) as { refreshToken?: string };
    return tokenFpSync(parsed?.refreshToken || '');
  } catch {
    return 'read_failed';
  }
}

/** read_auth_json IPC returns token only — file mtime is not exposed to the WebView. */
export async function readAuthJsonFpSnapshot(): Promise<{ fp: string; mtime: string }> {
  try {
    if (typeof window === 'undefined' || !('__TAURI__' in window)) {
      return { fp: 'no-tauri', mtime: 'no-tauri' };
    }
    const { readAuthJson } = await import('./connector');
    const creds = await readAuthJson();
    return {
      fp: tokenFpSync(creds?.refresh_token || ''),
      mtime: 'unavailable_via_ipc',
    };
  } catch {
    return { fp: 'read_failed', mtime: 'unavailable_via_ipc' };
  }
}

export function logBootTokenSnapshot(phase: string): void {
  try {
    const localStorage_fp = readLocalStorageRefreshFp();
    const now = Date.now();
    if (typeof window === 'undefined' || !('__TAURI__' in window)) {
      console.info(
        `[boot-token] phase=${phase} localStorage_fp=${localStorage_fp} authjson_fp=no-tauri authjson_mtime=no-tauri now=${now}`,
      );
      return;
    }
    void readAuthJsonFpSnapshot()
      .then(({ fp, mtime }) => {
        try {
          console.info(
            `[boot-token] phase=${phase} localStorage_fp=${localStorage_fp} authjson_fp=${fp} authjson_mtime=${mtime} now=${now}`,
          );
        } catch { /* noop */ }
      })
      .catch(() => {
        try {
          console.info(
            `[boot-token] phase=${phase} localStorage_fp=${localStorage_fp} authjson_fp=read_failed authjson_mtime=unavailable_via_ipc now=${now}`,
          );
        } catch { /* noop */ }
      });
  } catch { /* noop */ }
}

export type TokenRefreshReason =
  | '401_initiate_refresh'
  | '401_deduped_inflight'
  | 'upload_401_initiate_refresh'
  | 'upload_401_deduped_inflight';

/** Field marker on every forced refresh — fleet reports discriminate by reason= */
export function logForcedTokenRefresh(
  reason: TokenRefreshReason,
  url?: string,
  meta?: Record<string, unknown>,
): void {
  try {
    const urlPart = url ? ` url=${url}` : '';
    const metaPart = meta && Object.keys(meta).length > 0
      ? ` meta=${JSON.stringify(meta)}`
      : '';
    console.warn(`[TokenRefresh] reason=${reason}${urlPart}${metaPart}`);
  } catch { /* noop */ }
}

export function logRefreshRequested(caller: string, inflight: boolean, deduped: boolean): void {
  try {
    console.info(`[TokenRefresh] refresh requested by=${caller} inflight=${inflight} deduped=${deduped}`);
  } catch { /* noop */ }
}

/** Fleet beacon: N callers joined one in-flight /api/auth/refresh (#602). */
export function logRefreshSingleFlightCoalesced(waiters: number, caller?: string): void {
  try {
    const by = caller ? ` by=${caller}` : '';
    console.info(`[TokenRefresh] refresh_singleflight_coalesced waiters=${waiters}${by}`);
    diagnosticLog('info', 'other', 'refresh_singleflight_coalesced', { waiters, caller });
  } catch { /* noop */ }
}

/** Fleet beacon: a second POST with the same refresh token was blocked (#602). */
export function logRefreshDoubleAttemptBlocked(reason: string, fp?: string): void {
  try {
    const fpPart = fp ? ` fp=${fp}` : '';
    console.warn(`[TokenRefresh] refresh_double_attempt_blocked reason=${reason}${fpPart}`);
    diagnosticLog('warn', 'other', 'refresh_double_attempt_blocked', { reason, fp });
  } catch { /* noop */ }
}

export function logRefreshFailed(status: number, data: unknown, source?: string): void {
  try {
    const { errorCode, message } = parseRefreshErrorDetails(data);
    const suffix = source ? ` source=${source}` : '';
    console.warn(
      `[TokenRefresh] refresh failed status=${status} error_code=${errorCode} message=${message.slice(0, 240)}${suffix}`,
    );
  } catch { /* noop */ }
}

export function logAlreadyUsedAbsorbed(fp: string): void {
  try {
    console.info(`[Auth:lifecycle] already_used absorbed — session kept, adopted fp=${fp}`);
  } catch { /* noop */ }
}

/** Once-per-transition session-dead observability (js:auth channel). */
export function logSessionDeadTransition(
  phase: 'enter' | 'exit',
  fp: string,
  source?: string,
): void {
  try {
    const sourcePart = source ? ` source=${source}` : '';
    console.info(`[js:auth] session_dead_${phase} fp=${fp}${sourcePart}`);
  } catch { /* noop */ }
}

export function logSignoutDecision(
  errorCode: string,
  classified: RefreshFailureClass,
  status?: number,
): void {
  try {
    const localStorage_fp_now = readLocalStorageRefreshFp();
    const statusPart = status != null ? ` http_status=${status}` : '';
    if (typeof window === 'undefined' || !('__TAURI__' in window)) {
      console.warn(
        `[Auth:lifecycle] signout-decision error_code=${errorCode} localStorage_fp_now=${localStorage_fp_now} authjson_fp_now=no-tauri classified=${classified}${statusPart}`,
      );
      return;
    }
    void readAuthJsonFpSnapshot()
      .then(({ fp }) => {
        try {
          console.warn(
            `[Auth:lifecycle] signout-decision error_code=${errorCode} localStorage_fp_now=${localStorage_fp_now} authjson_fp_now=${fp} classified=${classified}${statusPart}`,
          );
        } catch { /* noop */ }
      })
      .catch(() => {
        try {
          console.warn(
            `[Auth:lifecycle] signout-decision error_code=${errorCode} localStorage_fp_now=${localStorage_fp_now} authjson_fp_now=read_failed classified=${classified}${statusPart}`,
          );
        } catch { /* noop */ }
      });
  } catch { /* noop */ }
}
